import React from "react";
import type { MapCanvasValue } from "../contexts/MapCanvasContext/MapCanvasContext.types";

type Animator = (map: MapCanvasValue) => () => void;
const Context = React.createContext({
  animate: (_map: MapCanvasValue) => {},
  register:
    (_animator: Animator): (() => void) =>
    () => {}
});

/** Each editor owns its animation; no transient positions enter map/history. */
export function ConstructionAnimationProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const animator = React.useRef<Animator | null>(null);
  const stop = React.useRef<(() => void) | null>(null);
  const cancel = React.useCallback(() => {
    stop.current?.();
    stop.current = null;
  }, []);
  const value = React.useMemo(
    () => ({
      animate(map: MapCanvasValue) {
        cancel();
        if (!window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches)
          stop.current = animator.current?.(map) ?? null;
      },
      register(next: Animator) {
        animator.current = next;
        return () => {
          if (animator.current === next) {
            cancel();
            animator.current = null;
          }
        };
      }
    }),
    [cancel]
  );
  React.useEffect(() => {
    // Restore the final render before a drag, edit, undo, save or floor switch.
    const motion = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    window.addEventListener("pointerdown", cancel, true);
    window.addEventListener("keydown", cancel, true);
    motion?.addEventListener("change", cancel);
    return () => {
      cancel();
      window.removeEventListener("pointerdown", cancel, true);
      window.removeEventListener("keydown", cancel, true);
      motion?.removeEventListener("change", cancel);
    };
  }, [cancel]);
  return <Context.Provider value={value}>{children}</Context.Provider>;
}
export const useConstructionAnimation = () => React.useContext(Context);
